import { useEffect, useRef, useState } from 'react';
import { extern, istPdfDatei } from './extern.ts';
import type { ReactNode } from 'react';
import { api } from '../api.ts';
import { Dialog } from './Formular.tsx';
import { Dateisymbol, type Kacheldatei } from './Dateikachel.tsx';
import { bytes, imFenster } from './dateiart.ts';
import {
  pfeile, wischEnde, wischStart, wischZweiterZeiger, type Wischstand,
} from './wischen.ts';
import {
  RADSCHRITT, ZURUECK, abstand, alsProzent, alsUmformung, darfWischen,
  doppeltipp, faktorGeklemmt, istDoppeltipp, istGezoomt, mitte, schieben,
  zoomAn, zoomRad,
  type Kasten, type Punkt, type Zoomstand,
} from './zoomen.ts';

/**
 * EINE DATEI IN EINEM FENSTER STATT IN EINEM NEUEN TAB.
 *
 * ORIGINALTEXT MARVIN (08.09.2026, Punkt 7)
 *   „Postfach: Design, Thumbnails der Anhänge, Anhänge in einem Fenster statt
 *    neuem Tab, Büroklammer statt Mailsymbol am Auftrag, verschobene Popups"
 *
 * EIN NEUER TAB IST EIN VERLASSENER VORGANG. Wer im Postfach eine Mail
 * bearbeitet und einen Anhang ansieht, steht danach in einem zweiten Fenster
 * mit einer PDF darin: die Liste ist weg, die Notiz, die er gerade schreiben
 * wollte, auch, und der Weg zurück ist die Rückkehr zum vorigen Tab. Es ist
 * dieselbe Beanstandung wie am 06.09.2026 zum Postfach selbst - „ein
 * Postfach, in dem man nach jeder Mail ein Fenster schliesst, ist ein
 * Aktenschrank und kein Postfach."
 *
 * DER NEUE TAB BLEIBT TROTZDEM ERREICHBAR, und zwar im Schaukopf. Er ist
 * der Weg zum HERUNTERLADEN, und dabei zählt der Dateiname: die Adresse trägt
 * ihn seit dem 27.08.2026 hinten mit, damit im Ordner nicht zwanzig UUIDs
 * liegen. **Ein Fenster, das den Weg nach draussen nimmt, ist eine
 * Sackgasse.**
 *
 * WAS SICH NICHT ZEIGEN LÄSST, SAGT ES. Eine Word-Datei in einem `<iframe>`
 * ergibt eine weisse Fläche oder einen Download, den niemand bestellt hat;
 * dort steht das gezeichnete Blatt und der Satz, was zu tun ist. **Eine
 * Fläche, die leer bleibt, ist von einem kaputten Fenster nicht zu
 * unterscheiden.**
 *
 * ## DER RAHMEN FÄLLT, UND ZWAR NUR WO ES EINEN INHALT GIBT
 *
 * ORIGINALTEXT MARVIN (15.09.2026)
 * > „bei dem öffnen der fotos brauchen wir keinen Rahmen. Es reicht das reine
 * >  Foto, dann mit Funktion links/rechts wischen und Zoom mit den Fingern
 * >  fehlt noch. Bei den pdf Dateien fehlt auch der Zoom. Da kann das popup
 * >  auxh ganz weg. Das nimmt nur Größe weg."
 *
 * `nackt` gilt für ein BILD und für eine PDF - dort ist der Inhalt die ganze
 * Auskunft, und jede Titelzeile nimmt ihm Fläche weg. Der Fall „lässt sich
 * hier nicht anzeigen" behält seinen Rahmen: dort steht eine MELDUNG, und
 * eine Meldung ohne Rahmen auf schwarzem Grund sieht aus wie ein Fehler.
 */
export function Dateifenster({
  datei, zu, blaettern,
}: {
  datei: Kacheldatei;
  zu: () => void;
  /** Mehrere Anhänge: vor, zurück und „2 von 5". */
  blaettern?: { stand: string; zurueck?: () => void; vor?: () => void };
}) {
  const art = imFenster(datei.mime, datei.dateiname);
  /* DIESELBE ADRESSE WIE AN DER KACHEL, und aus demselben Grund: wo der
     Aufrufer eine mitbringt (ein Anhang, der noch im Postfach liegt), gilt
     seine. Zwei Rechnungen über dieselbe Adresse zeigten im Fenster eine
     andere Datei als auf der Kachel, die es geöffnet hat. */
  const adresse = datei.adresse
    ?? api.dateiAdresse(datei.id, datei.dateiname);
  const groesse = datei.groesse === null || datei.groesse === undefined
    ? '' : bytes(Number(datei.groesse));
  const nackt = art === 'bild' || art === 'pdf' || art === 'video';

  /* DIE ZURÜCK-GESTE SCHLIESST DIE SCHAU (.288). Auf dem Telefon ist
     „zurück" (Wischen vom Rand, Zurück-Taste) der Weg aus einem Bild heraus.
     Ohne eigenen Eintrag im Verlauf verliess sie die ganze Seite. */
  const zuRef = useRef(zu);
  zuRef.current = zu;
  useEffect(() => {
    try { window.history.pushState({ dateifenster: true }, ''); } catch { /* ohne Verlauf geht es auch */ }
    const zurueck = () => zuRef.current();
    window.addEventListener('popstate', zurueck);
    return () => {
      window.removeEventListener('popstate', zurueck);
      try { if ((window.history.state as any)?.dateifenster) window.history.back(); } catch { /* dito */ }
    };
  }, []);

  return (
    /* AUF DEM GANZEN BILD UND NICHT IN EINEM KASTEN.
       ORIGINALTEXT MARVIN (11.09.2026): „zudem die dateivorschau beim öffnen
       muss auf dem kompletten bildschild sein am PC. so ist das popup zu
       klein und nimmt die übersicht weg."
       Ein Lageplan auf 1040 Bildpunkten ist ein Lageplan, den man nicht lesen
       kann - und wer ihn nicht lesen kann, lädt ihn herunter und macht ihn
       woanders auf. Dann ist die Vorschau ein Umweg. */
    <Dialog titel={datei.dateiname} untertitel={groesse || undefined}
            voll nackt={nackt} zu={zu}>
      {nackt && (
        /* SCHWEBEND NUR BEIM BILD. Bei der PDF liegt links der Seitenstreifen,
           und ein schwebender Kopf deckte dessen erstes Vorschaubild zu. */
        <Schaukopf datei={datei} groesse={groesse} adresse={adresse}
                   blaettern={blaettern} zu={zu} schwebend={art === 'bild'} />
      )}

      {/* `key` STATT EINES EIGENEN EFFEKTS. Beim Blättern muss der Zoom
          zurückgesetzt werden - sonst steht die nächste Datei in einer
          Vergrösserung da, die jemand für die vorige gewählt hat. Ein Effekt
          daneben wäre eine zweite Stelle, an der derselbe Zustand lebt; ein
          neuer Schlüssel baut das Bauteil neu auf, und damit steht der
          Anfangszustand wieder da. */}
      {art === 'bild' && (
        <Bildschau key={adresse} adresse={adresse} datei={datei}
                   blaettern={blaettern} />
      )}

      {/* EIN PDF WIRD VON UNS GEZEICHNET UND NICHT VOM BROWSER.
          ==================================================================
          ORIGINALTEXT MARVIN (15.09.2026, Bild 5)
            „links die vorschau ist unscharf"

          Er hatte recht, und der unscharfe Streifen war nicht unserer: der
          eingebaute Betrachter rastert seine Miniaturen in einer festen,
          niedrigen Auflösung und rechnet sie danach hoch. **An einem Bild,
          das ein fremdes Programm zeichnet, lässt sich nichts schärfer
          stellen.**

          DIESELBE LEHRE GILT FÜR DEN ZOOM. An einem `<iframe>` mit einer PDF
          darin lässt sich keiner hinterlegen: was darin geschieht, entscheidet
          der Betrachter des Browsers, und der hat seine eigenen Knöpfe an
          einer eigenen Leiste. Der Server rendert die Seiten ohnehin - also
          geht die aktuelle Seite als BILD durch dieselbe Zoomfläche wie ein
          Foto, und der eingebaute Betrachter bleibt der Rückfall für den
          Fall, dass der Server keine Seiten liefert. */}
      {art === 'pdf' && (
        <Pdfschau adresse={adresse} datei={datei} />
      )}

      {/* EIN VIDEO SPIELT DER BROWSER (.386). Kein Zoom, keine Seiten: der
          eingebaute Spieler mit seinen Knöpfen, auf der ganzen Fläche. */}
      {art === 'video' && (
        <div className="dateischau video" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#000' }}>
          <video key={adresse} src={adresse} controls playsInline preload="metadata"
                 style={{ maxWidth: '100%', maxHeight: '100%' }} />
        </div>
      )}

      {art === null && (
        <>
          <div className="dateischau leer">
            <Dateisymbol name={datei.dateiname} />
            <p className="klein leise">
              Diese Datei lässt sich hier nicht anzeigen. Über „Herunterladen"
              öffnet sie das Programm, das dafür da ist - unter ihrem eigenen
              Namen.
            </p>
          </div>

          <div className="dialogfuss">
            <a className="knopf" href={adresse} target="_blank" rel="noreferrer">
              Herunterladen
            </a>
            <button type="button" className="knopf haupt" onClick={zu}>
              Schließen
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}


/**
 * DER KOPF, DER ÜBER DEM INHALT SCHWEBT.
 *
 * Er trägt, was der Rahmen getragen hat, und nimmt dafür keine Fläche: er
 * liegt ÜBER dem Bild und nicht darüber im Raster. Was darin steht, ist
 * genau das, was ohne ihn verlorenginge - der Name (eine Datei ohne Namen
 * ist auf dem Telefon stumm), die Grösse, der Stand beim Blättern, der Weg
 * zum Herunterladen und der zum Schliessen.
 *
 * ## ER HAT 135 VON 667 PUNKTEN GENOMMEN, UND DAS WAREN 20 PROZENT
 *
 * ORIGINALTEXT MARVIN (15.09.2026)
 * > „bei dem öffnen der fotos brauchen wir keinen Rahmen. Es reicht das reine
 * >  Foto ... Das nimmt nur Größe weg."
 *
 * GEMESSEN AM 16.09.2026 AN EINEM IPHONE SE (375x667): der Kopf war 135
 * Punkte hoch, die Fläche darunter fing bei 135 an, und das Foto hatte 532
 * statt 667. **Der Rahmen war weg und der Streifen war geblieben** - und 20
 * Prozent sind genau das, was Marvins Satz meint.
 *
 * Er war deshalb so hoch, weil er DREI Zeilen brauchte: Name über Grösse,
 * darunter „&larr; vorige / 1 von 2 / nächste &rarr;", darunter
 * „Herunterladen" und „Schließen". Jede Aufschrift kostet auf 375 Punkten
 * eine eigene Zeile.
 *
 * ZWEI SACHEN ÄNDERN DAS, UND KEINE DAVON NIMMT EINEN WEG WEG:
 *
 *  1. DIE AUFSCHRIFT WEICHT AUF SCHMALEN BILDSCHIRMEN DEM ZEICHEN. Die
 *     Knöpfe tragen ihr Wort weiter im Text, das Stylesheet blendet es unter
 *     700 Punkten aus, und daneben steht ein Zeichen mit `aria-hidden`.
 *     **Der Knopf trägt zusätzlich `aria-label`, also ist er in jedem Fall
 *     benannt** - eine Vorlesehilfe liest „Herunterladen", ob das Wort zu
 *     sehen ist oder nicht. Eine Schwelle an der Breite ist hier erlaubt,
 *     weil beide Seiten davon bedienbar sind; verboten ist die Schwelle, die
 *     eine HÖHE in Punkten festnagelt (siehe unten).
 *  2. DIE GRÖSSE STEHT NEBEN DEM NAMEN UND NICHT DARUNTER. Sie ist 6 Zeichen
 *     lang und hat nie eine eigene Zeile verdient.
 *
 * ## UND BEIM BILD SCHWEBT ER WIRKLICH
 *
 * `schwebend` legt ihn aus dem Fluss ÜBER die Schau, und dann hat das Foto
 * die volle Höhe. **Das gilt nur für das Bild und nicht für die PDF:** dort
 * liegt links der Seitenstreifen, und ein schwebender Kopf deckte dessen
 * erstes Vorschaubild zu. Genau diese Unterscheidung hat am 15.09.2026
 * gefehlt - der Grund gegen das Schweben galt der PDF und wurde auf das Foto
 * mit übertragen.
 *
 * Was NICHT zurückkommt, ist ein Abstand in der Höhe dieser Zeile: das wäre
 * eine ZAHL, die von der Breite des Dateinamens abhängt und beim ersten
 * Umbruch falsch ist. Beim Foto braucht es sie nicht, weil darunter nichts
 * ausweichen muss - das Bild steht mittig in der ganzen Fläche.
 *
 * DIE WEGE STEHEN VOR DEM BLÄTTERN, und das entscheidet, wo sie nach einem
 * Umbruch landen: „Schließen" sitzt oben rechts, wo jeder es sucht, und die
 * Blätterreihe rutscht in die zweite Zeile. Andersherum stand der Weg aus dem
 * Fenster unten links unter einem Foto - **ein Ausgang, den man suchen muss,
 * ist auf einem Telefon keiner.** Die Tastaturfolge liest sich genauso:
 * Herunterladen, Schließen, vorige, nächste.
 *
 * DIE BLÄTTERKNÖPFE STEHEN HIER UND NICHT NUR ALS PFEILE IM BILD. Die Pfeile
 * tragen `aria-hidden` und stehen nicht in der Tastaturfolge; ohne diese
 * beiden Knöpfe gäbe es zum Blättern keinen Weg mit der Tastatur - **eine
 * Geste ist ein zweiter Weg und nie der einzige.**
 *
 * UND ER DARF WEITER UMBRECHEN. Auf einem schmalen Bildschirm passt ein
 * langer Dateiname nicht neben die Bedienelemente; die Alternative wäre ein
 * abgeschnittener Name, und **ein Name gehört an das, was er benennt** -
 * `IMG_20240513_1015` und `IMG_20240513_1042` unterscheiden sich in genau
 * den Zeichen, die ein „..." frisst.
 */
function Schaukopf({
  datei, groesse, adresse, blaettern, zu, schwebend,
}: {
  datei: Kacheldatei;
  groesse: string;
  adresse: string;
  blaettern?: { stand: string; zurueck?: () => void; vor?: () => void };
  zu: () => void;
  /** Über der Schau statt darüber im Fluss. Beim Bild ja, bei der PDF nein. */
  schwebend?: boolean;
}) {
  return (
    <div className={'schaukopf' + (schwebend ? ' schwebend' : '')}>
      <div className="schaukopfname">
        <span className="schaudateiname" title={datei.dateiname}>
          {datei.dateiname}
        </span>
        {groesse && <span className="klein schaugroesse">{groesse}</span>}
      </div>

      <div className="reihe schaukopfwege">
        <a className="knopf klein" href={adresse} target="_blank" rel="noreferrer"
           aria-label="Herunterladen" title="Herunterladen">
          <span aria-hidden>&darr;</span>
          <span className="wortaufbreit"> Herunterladen</span>
        </a>
        <button type="button" className="knopf klein haupt" onClick={zu}
                aria-label="Schließen" title="Schließen">
          <span aria-hidden>&times;</span>
          <span className="wortaufbreit"> Schließen</span>
        </button>
      </div>

      {blaettern && (
        <div className="reihe schaublaettern">
          <button type="button" className="knopf klein"
                  aria-label="Vorige Datei" title="Vorige Datei"
                  disabled={!blaettern.zurueck}
                  onClick={() => blaettern.zurueck?.()}>
            <span aria-hidden>&larr;</span>
            <span className="wortaufbreit"> vorige</span>
          </button>
          <span className="klein">{blaettern.stand}</span>
          <button type="button" className="knopf klein"
                  aria-label="Nächste Datei" title="Nächste Datei"
                  disabled={!blaettern.vor}
                  onClick={() => blaettern.vor?.()}>
            <span className="wortaufbreit">nächste </span>
            <span aria-hidden>&rarr;</span>
          </button>
        </div>
      )}
    </div>
  );
}


/**
 * DIE FLÄCHE, AUF DER GEZOOMT UND GEWISCHT WIRD.
 *
 * ORIGINALTEXT MARVIN (15.09.2026)
 * > „Es reicht das reine Foto, dann mit Funktion links/rechts wischen und
 * >  Zoom mit den Fingern fehlt noch. Bei den pdf Dateien fehlt auch der
 * >  Zoom. ... Und der Zoom muss auch hinterlegt werden dort."
 *
 * SIE IST EINE UND GILT FÜR BEIDES. Ein Foto und eine gerenderte PDF-Seite
 * sind für diese Fläche dasselbe: ein Bild in einem Kasten. Zwei Fassungen
 * liefen beim ersten Nachtrag auseinander, und dann zoomt das eine anders als
 * das andere.
 *
 * ## DIE REGELN STEHEN IN `zoomen.ts` UND NICHT HIER
 *
 * Wohin ein Bild rutscht, wenn zwei Finger es um den Faktor 2,4 aufziehen,
 * ist eine Rechnung - und eine Datei mit JSX lässt sich mit
 * `--experimental-strip-types` nicht laden, dann bliebe nur die Textsuche.
 * **Eine Textsuche misst, dass etwas dasteht, nicht, dass es stimmt.** Hier
 * steht die Verdrahtung und sonst nichts.
 *
 * ## DAS RAD HÄNGT VON HAND AM KNOTEN
 *
 * React hängt `wheel` PASSIV am Wurzelknoten an, und ein passiver Zuhörer
 * darf nicht abbrechen: `preventDefault()` wird stillschweigend übergangen,
 * ohne Fehler und ohne Meldung in der Konsole. Dieses Haus hat das am
 * 02.09.2026 an der Tiefbaukarte bezahlt. Der Zuhörer wird deshalb von Hand
 * angehängt (`{ passive: false }`), in einem Effekt OHNE Abhängigkeiten - was
 * er braucht, liest er beim Auslösen aus einem MERKER. Eine Abhängigkeitsliste
 * mit dem Zustand darin hiesse, den Zuhörer bei jedem Zeichnen ab- und wieder
 * anzumelden.
 *
 * ## DER ZUSTAND LIEGT IN EINEM MERKER UND ZUSÄTZLICH IM ZUSTAND
 *
 * Ein Rückruf, der einen State LIEST, liest ihn zu früh: React setzt ihn erst
 * NACH dem Ereignis, und der nächste Zeigerruf sähe den Stand von davor -
 * vierter Fall nach `useEntwurf` (31.08.2026), `Dialog` (02.09.2026) und den
 * sechs Kästchen des zweiten Faktors (09.09.2026). Gerechnet wird deshalb mit
 * `standRef`, gezeichnet mit `stand`.
 *
 * ## UND DER FANG GILT NUR AUSSERHALB DER KNÖPFE
 *
 * `setPointerCapture` zieht jedes Ereignis an den fangenden Knoten, und damit
 * landet auch der KLICK dort statt am Knopf - **ein Pfeil, der aussieht wie
 * ein Knopf und auf ein Tippen nicht antwortet, ist von einem kaputten nicht
 * zu unterscheiden.**
 */
function Zoomflaeche({
  blaettern, kennzeichen, children,
}: {
  /** Wohin ein Wisch führt. Im Bild die nächste DATEI, in der PDF die
      nächste SEITE - wer in einem Dokument wischt, meint die Seite. */
  blaettern?: { zurueck?: () => void; vor?: () => void };
  /** Was die Fläche zeigt, für die Vorlesehilfe. */
  kennzeichen?: string;
  children: ReactNode;
}) {
  const kasten = useRef<HTMLDivElement | null>(null);
  const standRef = useRef<Zoomstand>({ ...ZURUECK });
  const [stand, setStandAnzeige] = useState<Zoomstand>({ ...ZURUECK });
  const zeiger = useRef<Map<number, Punkt>>(new Map());
  const zug = useRef<Wischstand | null>(null);
  const kniff = useRef<{ weite: number; stand: Zoomstand; mitte: Punkt } | null>(null);
  const letzterTipp = useRef<{ zeit: number; p: Punkt } | null>(null);
  /* Die Blätterwege liegen in einem Merker, weil der Rad-Effekt sie nicht in
     seiner Abhängigkeitsliste tragen darf: an jeder Aufrufstelle sind sie ein
     an Ort und Stelle gebauter Pfeil, also bei jedem Zeichnen ein NEUES
     Objekt. */
  const blaetternRef = useRef(blaettern);
  blaetternRef.current = blaettern;

  const setze = (s: Zoomstand) => { standRef.current = s; setStandAnzeige(s); };
  const masse = (): Kasten => {
    const r = kasten.current?.getBoundingClientRect();
    return { breite: r?.width || 1, hoehe: r?.height || 1 };
  };
  const imKasten = (e: { clientX: number; clientY: number }): Punkt => {
    const r = kasten.current?.getBoundingClientRect();
    return { x: e.clientX - (r?.left ?? 0), y: e.clientY - (r?.top ?? 0) };
  };

  useEffect(() => {
    const k = kasten.current;
    if (!k) return;
    const rad = (e: WheelEvent) => {
      e.preventDefault();
      const r = k.getBoundingClientRect();
      setze(zoomRad(
        standRef.current,
        e.deltaY,
        { x: e.clientX - r.left, y: e.clientY - r.top },
        { breite: r.width || 1, hoehe: r.height || 1 },
      ));
    };
    k.addEventListener('wheel', rad, { passive: false });
    return () => { k.removeEventListener('wheel', rad); };
  }, []);

  const zoomKnopf = (f: number) => {
    const k = masse();
    setze(zoomAn(standRef.current, standRef.current.faktor * f,
                 { x: k.breite / 2, y: k.hoehe / 2 }, k));
  };

  const p = pfeile(blaettern);
  const gezoomt = istGezoomt(stand);

  return (
    <div
      className="zoomflaeche"
      ref={kasten}
      onPointerDown={(e) => {
        zeiger.current.set(e.pointerId, imKasten(e));
        if (zeiger.current.size >= 2) {
          const [a, b] = [...zeiger.current.values()];
          kniff.current = {
            weite: abstand(a, b), stand: standRef.current, mitte: mitte(a, b),
          };
          /* ZWEI FINGER SIND KEIN WISCH. Ohne diese Zeile bliebe der Zug des
             ersten Fingers stehen und löste beim Loslassen ein Blättern aus -
             mitten im Aufziehen. */
          if (zug.current) zug.current = wischZweiterZeiger(zug.current);
          return;
        }
        if (darfWischen(standRef.current)) {
          zug.current = wischStart(e.pointerId, e.clientX, e.clientY);
        }
        const aufKnopf = (e.target as Element | null)
          ?.closest?.('.wischpfeil, .zoomleiste');
        if (aufKnopf) return;
        /* Ein Fang, der nicht geht, ist kein Fehler: dann endet der Zug eben
           am Rand des Kastens. Er darf nur nichts umbringen. */
        try { kasten.current?.setPointerCapture(e.pointerId); } catch { /* egal */ }
      }}
      onPointerMove={(e) => {
        if (!zeiger.current.has(e.pointerId)) return;
        const vorher = zeiger.current.get(e.pointerId)!;
        const jetzt = imKasten(e);
        zeiger.current.set(e.pointerId, jetzt);
        const k = masse();

        const kn = kniff.current;
        if (zeiger.current.size >= 2 && kn && kn.weite > 0) {
          const [a, b] = [...zeiger.current.values()];
          const ziel = kn.stand.faktor * (abstand(a, b) / kn.weite);
          const m = mitte(a, b);
          /* ERST ZOOMEN, DANN DIE MITTE NACHFÜHREN. Wer `zoomAn` mit der
             LAUFENDEN Mitte ruft, mischt beide Bewegungen: das Aufziehen
             rechnet dann um einen Punkt, der sich selbst gerade verschiebt,
             und das Bild wandert unter den Fingern weg. */
          const gez = zoomAn(kn.stand, ziel, kn.mitte, k);
          setze(schieben(gez, m.x - kn.mitte.x, m.y - kn.mitte.y, k));
          return;
        }

        if (istGezoomt(standRef.current)) {
          setze(schieben(standRef.current,
                         jetzt.x - vorher.x, jetzt.y - vorher.y, k));
        }
      }}
      onPointerUp={(e) => {
        const waren = zeiger.current.size;
        zeiger.current.delete(e.pointerId);
        try { kasten.current?.releasePointerCapture(e.pointerId); } catch { /* egal */ }
        if (waren >= 2) { kniff.current = null; zug.current = null; return; }
        kniff.current = null;

        const p2 = imKasten(e);
        const jetzt = { zeit: Date.now(), p: p2 };
        if (istDoppeltipp(letzterTipp.current, jetzt)) {
          letzterTipp.current = null;
          zug.current = null;
          setze(doppeltipp(standRef.current, p2, masse()));
          return;
        }
        letzterTipp.current = jetzt;

        const wohin = wischEnde(zug.current, e.pointerId, e.clientX, e.clientY);
        zug.current = null;
        /* SOLANGE GEZOOMT IST, BLÄTTERT KEIN WISCH. Ohne diesen Riegel
           wechselt jedes Verschieben eines vergrösserten Bildes die Datei -
           und zwar genau dann, wenn jemand sehr genau hinsieht. */
        if (!darfWischen(standRef.current)) return;
        if (wohin === 'vor') blaetternRef.current?.vor?.();
        if (wohin === 'zurueck') blaetternRef.current?.zurueck?.();
      }}
      onPointerCancel={(e) => {
        zeiger.current.delete(e.pointerId);
        kniff.current = null;
        zug.current = null;
      }}
    >
      <div className="zoomzug" style={{ transform: alsUmformung(stand) }}>
        {children}
      </div>

      {/* DIE PFEILE VERSCHWINDEN, SOLANGE GEZOOMT IST. Sie stehen für eine
          Geste, die dann nicht geht - und ein Knopf, der auf ein Tippen
          nichts tut, ist von einem kaputten nicht zu unterscheiden. */}
      {!gezoomt && p.zurueck && (
        <button type="button" className="wischpfeil links"
                aria-hidden tabIndex={-1}
                onClick={() => blaettern?.zurueck?.()}>
          <Doppelpfeil nach="links" />
        </button>
      )}
      {!gezoomt && p.vor && (
        <button type="button" className="wischpfeil rechts"
                aria-hidden tabIndex={-1}
                onClick={() => blaettern?.vor?.()}>
          <Doppelpfeil nach="rechts" />
        </button>
      )}

      {/* DER ZOOM HAT AUCH EINEN KNOPF. Zwei Finger hat nur, wer ein Telefon
          in der Hand hält; am Schreibtisch gibt es das Rad, und wer weder
          Rad noch Finger benutzt, braucht einen Weg, den er sieht. Die
          Aufschrift ist zugleich die Auskunft, wo man steht. */}
      <div className="zoomleiste">
        <button type="button" className="zoomknopf"
                aria-label="Kleiner" onClick={() => zoomKnopf(1 / 1.4)}>
          &minus;
        </button>
        <button type="button" className="zoomknopf zoomstand"
                aria-label={'Zoom ' + alsProzent(stand) + ', zurücksetzen'}
                onClick={() => setze({ ...ZURUECK })}>
          {alsProzent(stand)}
        </button>
        <button type="button" className="zoomknopf"
                aria-label="Größer" onClick={() => zoomKnopf(1.4)}>
          +
        </button>
      </div>

      {kennzeichen && <span className="nurvorlesen">{kennzeichen}</span>}
    </div>
  );
}


/**
 * EIN BILD, DAS MAN WISCHEN UND ZOOMEN KANN - UND ZWEI PFEILE, DIE ES SAGEN.
 *
 * ORIGINALTEXT MARVIN (15.09.2026, Bild 6)
 *   „bei der vorschau von bildern, da hast du oben zwar die buttons, aber man
 *    ist ja «wishen» auf dem smartphone gewohnt. baue das ein mit zwei
 *    leichten doppelpfeilen im bild damit man weiß das man wischen kann."
 *
 * `draggable={false}` IST KEIN SCHMUCK. Ein Browser zieht ein Bild von sich
 * aus als Datei; das bricht die Zeigerfolge mittendrin ab, und der Zug endet
 * nie. Auf dem Telefon fällt es nicht auf, an der Maus sofort.
 */
function Bildschau({
  adresse, datei, blaettern,
}: {
  adresse: string;
  datei: Kacheldatei;
  blaettern?: { stand: string; zurueck?: () => void; vor?: () => void };
}) {
  return (
    <Zoomflaeche blaettern={blaettern}>
      <img className="zoombild" src={adresse} alt={datei.dateiname}
           draggable={false} />
    </Zoomflaeche>
  );
}

/**
 * ZWEI WINKEL, GEZEICHNET UND NICHT GESCHRIEBEN.
 *
 * Als Schriftzeichen (》 oder ») hinge die Gestalt an der Schrift des Geräts,
 * und auf dem Telefon steht dort oft ein Anführungszeichen - genau die
 * Verwechslung, die dieses Haus mit `«` ohnehin führt. Zwei Striche sind zwei
 * Striche.
 */
function Doppelpfeil({ nach }: { nach: 'links' | 'rechts' }) {
  const d = nach === 'links'
    ? 'M13 4 L7 10 L13 16 M19 4 L13 10 L19 16'
    : 'M7 4 L13 10 L7 16 M13 4 L19 10 L13 16';
  return (
    <svg viewBox="0 0 26 20" width="26" height="20" focusable="false">
      <path d={d} fill="none" stroke="currentColor" strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}


/**
 * DIE PDF GEHT AN DEN BETRACHTER DES BROWSERS.
 *
 * ORIGINALTEXT MARVIN (21.09.2026)
 *   „Die PDF dateien lassen sich immernoch nicht richtig nach unten scrollen.
 *    bitte baue diese funktion wieder aus. wir nutzen die software vom PC und
 *    handy."
 *
 * ============================================================================
 * ER HAT RECHT, UND ZWAR ZUM DRITTEN MAL AN DERSELBEN SACHE
 * ============================================================================
 *
 * Seit .164 zeichnete diese Plattform die PDF selbst: der Server rendert die
 * Seiten zu Bildern, ein Streifen links zum Blättern, das Bild in einer
 * eigenen Fläche mit eigenem Zoom. Gebaut wurde das, weil am 15.09.2026 der
 * Zoom fehlte - an einem Betrachter, den ein fremdes Programm zeichnet, lässt
 * sich keiner hinterlegen.
 *
 * Das Rollen ist daran zweimal gescheitert (18.09. und 21.09.2026), und beim
 * zweiten Mal hat Marvin nicht nach einer dritten Behebung gefragt, sondern
 * nach dem Ausbau.
 *
 * > **WER EINEN BETRACHTER NACHBAUT, BAUT AUCH SEINE JAHRE AN ARBEIT NACH.**
 * > Rollen, Zoom, Tastatur, Suche, Drucken, Textauswahl, Bedienhilfen: der
 * > eingebaute kann das alles, auf jedem Gerät, und wir haben zwei Anläufe
 * > gebraucht, um die erste dieser Sachen wiederzubekommen.
 *
 * Was hier steht, ist deshalb ein `<iframe>` mit der Datei darin und sonst
 * nichts. Der Zoom ist wieder der des Browsers - auf dem Telefon die Geste
 * mit zwei Fingern, am Rechner Strg und Mausrad -, und das Rollen ebenso.
 *
 * WAS DAMIT WEGFÄLLT, STEHT HIER, DAMIT NIEMAND ES SUCHT: der Seitenstreifen
 * zum Blättern, das seitenweise Rendern am Server, die Überabtastung und der
 * eigene Zoom auf der Seite. Die Wege am Server (`dateiSeiten`,
 * `dateiSeiteAdresse`) bleiben stehen - die Vorschaubilder der Kacheln leben
 * davon.
 */
function Pdfschau(
  { adresse, datei }: { adresse: string; datei: Kacheldatei }
) {
  /* KEINE EINBETTUNG MEHR (.234), siehe extern.ts. Hierher kommt man nur
     noch beim Blättern durch mehrere Anhänge: die PDF steht als Blatt da und
     geht mit einem Tipp im Programm des Geräts auf. */
  return (
    <div className="dateischau leer pdfextern">
      <Dateisymbol name={datei.dateiname} />
      <p className="klein leise">
        PDF-Dateien öffnen im PDF-Programm des Geräts.
      </p>
      <button type="button" className="knopf haupt" onClick={() => extern(adresse)}>
        PDF öffnen
      </button>
    </div>
  );
}


/**
 * Das Fenster, wo es gebraucht wird - mit EINEM Haken statt mit drei Merkern.
 *
 * DER HAKEN STEHT AM ANFANG DES BAUTEILS und läuft bei jedem Durchgang. Ein
 * Haken hinter einer bedingten Rückgabe ist auf dem Telefon ein weisser
 * Bildschirm (React-Fehler 310) - das hat der Burger am 05.09.2026 gekostet.
 */
export function useDateifenster(): {
  fenster: ReactNode;
  zeige: (d: Kacheldatei, liste?: Kacheldatei[]) => void;
} {
  const [stand, setStand] = useState<
    { liste: Kacheldatei[]; nr: number } | null>(null);

  const zeige = (d: Kacheldatei, liste?: Kacheldatei[]) => {
    /* EINE PDF GEHT DIREKT IM PROGRAMM DES GERÄTS AUF (.234), ohne Fenster
       in der Plattform dazwischen. */
    if (istPdfDatei(d.mime, d.dateiname)) {
      extern(d.adresse ?? api.dateiAdresse(d.id, d.dateiname));
      return;
    }
    const l = liste && liste.length ? liste : [d];
    const nr = Math.max(0, l.findIndex((x) => x.id === d.id));
    setStand({ liste: l, nr });
  };

  const datei = stand ? stand.liste[stand.nr] : null;
  const fenster = stand && datei ? (
    <Dateifenster
      datei={datei}
      zu={() => setStand(null)}
      blaettern={stand.liste.length > 1 ? {
        stand: `${stand.nr + 1} von ${stand.liste.length}`,
        zurueck: stand.nr > 0
          ? () => setStand({ ...stand, nr: stand.nr - 1 }) : undefined,
        vor: stand.nr < stand.liste.length - 1
          ? () => setStand({ ...stand, nr: stand.nr + 1 }) : undefined,
      } : undefined}
    />
  ) : null;

  return { fenster, zeige };
}
